import formSchema from '@genfun/core/data/formSchema.json';

// Helpers that walk the shared form schema (stage -> option -> next stage) for the photo wizard.

export function findOptionIndex(stage, promptKey, promptValue) {
    const idx = stage.options.findIndex((opt) => opt.data?.prompt?.[promptKey] === promptValue);
    if (idx >= 0) return idx;
    const simplify = (v) => v.replace(/(.)\1+/g, '$1');
    return stage.options.findIndex((opt) => {
        const val = opt.data?.prompt?.[promptKey];
        return val && simplify(val) === simplify(promptValue);
    });
}

const stagePromptKey = (stage) => Object.keys(stage.options[0]?.data?.prompt || {})[0];

// Clothes / location / pose-skip stages are answered by the picker cards, not by the girl wizard.
// Returns the next stage name, or undefined when `name` is a stage the user has to answer.
function autoAnswer(name, pose, clothesIdx, locationIdx, formRef) {
    let idx;
    if (name.startsWith('stage_clothes_')) idx = clothesIdx;
    else if (name.startsWith('stage_location_')) idx = locationIdx;
    else if (name in (pose.skip || {})) idx = pose.skip[name];
    else return undefined;
    if (formRef) formRef.current[name] = idx;
    return formSchema[name]?.options[idx]?.next ?? null;
}

export function isPresetCompatible(preset, pose) {
    let name = pose.next;
    while (name && name !== 'generate') {
        const auto = autoAnswer(name, pose, 0, 0, null);
        if (auto !== undefined) { name = auto; continue; }
        const stage = formSchema[name];
        if (!stage) return false;
        const key = stagePromptKey(stage);
        if (!key || preset.values[key] === undefined) return false;
        const idx = findOptionIndex(stage, key, preset.values[key]);
        if (idx < 0) return false;
        name = stage.options[idx].next;
    }
    return true;
}

export function resetFormRef(formRef, pose) {
    Object.keys(formRef.current).forEach((k) => { if (k.startsWith('stage')) delete formRef.current[k]; });
    Object.assign(formRef.current, pose.formRef);
    Object.entries(pose.skip).forEach(([stageName, idx]) => { formRef.current[stageName] = idx; });
}

// Fills every stage from a preset's values.
export function applyPreset(formRef, pose, preset, clothesIdx, locationIdx) {
    let name = pose.next;
    while (name && name !== 'generate') {
        const auto = autoAnswer(name, pose, clothesIdx, locationIdx, formRef);
        if (auto !== undefined) { name = auto; continue; }
        const stage = formSchema[name];
        if (!stage) break;
        const key = stagePromptKey(stage);
        const idx = key && preset.values[key] !== undefined ? findOptionIndex(stage, key, preset.values[key]) : -1;
        if (idx < 0) break;
        formRef.current[name] = idx;
        name = stage.options[idx].next;
    }
}

// Follows `next` from a chosen option, auto-answering picker stages.
// Returns the next stage the user must answer, or null when the form is complete.
export function nextGirlStage(formRef, pose, startName, clothesIdx, locationIdx) {
    let name = startName;
    while (name && name !== 'generate') {
        const auto = autoAnswer(name, pose, clothesIdx, locationIdx, formRef);
        if (auto !== undefined) { name = auto; continue; }
        return formSchema[name] || null;
    }
    return null;
}

export const firstGirlStage = (pose) => formSchema[pose.next] || null;
